import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Archive, ClipboardList, DollarSign, Package, Plus, Receipt, Trash2, Truck, Users, X, XCircle } from 'lucide-react';
import { formatCurrency } from '../lib/format';

const InventoryModule = ({ products, loading, onUpdateStock, readOnly }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStock, setFilterStock] = useState('all');
  const [stockModal, setStockModal] = useState(null); // { product, mode: 'add'|'subtract' }
  const [stockAmount, setStockAmount] = useState('');

  const handleStockSubmit = () => {
    if (!stockModal || !stockAmount || parseInt(stockAmount) <= 0) return;
    const amount = parseInt(stockAmount);
    const adjustment = stockModal.mode === 'add' ? amount : -amount;
    onUpdateStock(stockModal.product.id, adjustment);
    setStockModal(null);
    setStockAmount('');
  };

  const filteredProducts = products.filter(p => {
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!p.name?.toLowerCase().includes(query) && 
          !p.category?.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // Filter by stock level
    if (filterStock === 'low' && p.stock >= 10) return false;
    if (filterStock === 'normal' && (p.stock < 10 || p.stock > 50)) return false;
    if (filterStock === 'high' && p.stock <= 50) return false;
    
    return true;
  });

  const getStockStatus = (stock) => {
    if (stock <= 0) return { label: 'Habis', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' };
    if (stock < 10) return { label: 'Rendah', color: 'bg-rose-50 text-rose-600', dotColor: 'bg-rose-500' };
    if (stock <= 50) return { label: 'Normal', color: 'bg-emerald-50 text-emerald-600', dotColor: 'bg-emerald-500' };
    return { label: 'Tinggi', color: 'bg-blue-50 text-blue-600', dotColor: 'bg-blue-500' };
  };

  const totalProducts = products.length;
  const lowStockProducts = products.filter(p => p.stock > 0 && p.stock < 10).length;
  const outOfStockProducts = products.filter(p => p.stock <= 0).length;
  const totalStockValue = products.reduce((sum, p) => sum + (p.stock * p.price), 0);

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">Inventory</h2>
          <p className="text-muted-foreground mt-1">Pantau stok produk Anda secara real-time</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-xl">
              <Package className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Produk</p>
              <p className="text-xl font-bold">{totalProducts}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-xl">
              <AlertTriangle className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Stok Rendah</p>
              <p className="text-xl font-bold text-amber-600">{lowStockProducts}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-50 rounded-xl">
              <XCircle className="text-rose-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Stok Habis</p>
              <p className="text-xl font-bold text-rose-600">{outOfStockProducts}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-xl">
              <DollarSign className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Nilai Inventory</p>
              <p className="text-lg font-bold">{formatCurrency(totalStockValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="relative w-full lg:w-80">
            <input
              type="text"
              placeholder="Cari produk..."
              className="input-field pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-inventory"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Filter:</span>
            <div className="flex gap-1 flex-wrap">
              <button 
                onClick={() => setFilterStock('all')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filterStock === 'all' ? 'bg-primary text-primary-foreground' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Semua
              </button>
              <button 
                onClick={() => setFilterStock('low')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filterStock === 'low' ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Rendah
              </button>
              <button 
                onClick={() => setFilterStock('normal')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filterStock === 'normal' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Normal
              </button>
              <button 
                onClick={() => setFilterStock('high')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filterStock === 'high' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Tinggi
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th className="px-4 py-4 text-left" style={{width: '50px'}}>No</th>
                <th className="px-4 py-4 text-left">Produk</th>
                <th className="px-4 py-4 text-left" style={{width: '100px'}}>Kategori</th>
                <th className="px-4 py-4 text-center" style={{width: '70px'}}>Stok</th>
                <th className="px-4 py-4 text-center" style={{width: '70px'}}>Satuan</th>
                <th className="px-4 py-4 text-right" style={{width: '130px'}}>Harga</th>
                <th className="px-4 py-4 text-right" style={{width: '140px'}}>Nilai Stok</th>
                <th className="px-4 py-4 text-center" style={{width: '90px'}}>Status</th>
                {!readOnly && <th className="px-4 py-4 text-center" style={{width: '80px'}}>Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={readOnly ? "8" : "9"} className="px-4 py-12 text-center text-muted-foreground">Memuat data...</td></tr>
              ) : filteredProducts.length === 0 ? (
                <tr><td colSpan={readOnly ? "8" : "9"} className="px-4 py-12 text-center text-muted-foreground">
                  {searchQuery || filterStock !== 'all' ? 'Tidak ada produk yang sesuai filter.' : 'Belum ada produk.'}
                </td></tr>
              ) : filteredProducts.map((p, index) => {
                const stockStatus = getStockStatus(p.stock);
                const stockValue = p.stock * p.price;
                return (
                  <tr key={`inv-${p.id}-${index}`} className="table-row">
                    <td className="px-4 py-3 text-muted-foreground">{index + 1}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{p.name}</p>
                      {p.description && <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-xs">{p.category || '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${p.stock < 10 ? 'text-rose-600' : p.stock > 50 ? 'text-blue-600' : 'text-emerald-600'}`}>
                        {p.stock}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-muted-foreground">{p.unit}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(p.price)}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatCurrency(stockValue)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${stockStatus.dotColor}`}></span>
                        {stockStatus.label}
                      </span>
                    </td>
                    {!readOnly && (
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => { setStockModal({ product: p, mode: 'add' }); setStockAmount(''); }}
                          className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                          title="Tambah Stok"
                          data-testid={`stock-add-${p.id}`}
                        >
                          <Plus size={14} />
                        </button>
                        <button
                          onClick={() => { setStockModal({ product: p, mode: 'subtract' }); setStockAmount(''); }}
                          className="p-1.5 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                          title="Kurangi Stok"
                          data-testid={`stock-subtract-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {filteredProducts.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan="3" className="px-4 py-3 text-right">Total</td>
                  <td className="px-4 py-3 text-center font-bold">
                    {filteredProducts.reduce((sum, p) => sum + p.stock, 0)}
                  </td>
                  <td></td>
                  <td></td>
                  <td className="px-4 py-3 text-right font-bold">
                    {formatCurrency(filteredProducts.reduce((sum, p) => sum + (p.stock * p.price), 0))}
                  </td>
                  <td></td>
                  {!readOnly && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Stock Update Modal */}
      <AnimatePresence>
        {stockModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setStockModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="stock-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">
                  {stockModal.mode === 'add' ? 'Tambah Stok' : 'Kurangi Stok'}
                </h3>
                <button onClick={() => setStockModal(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-sm text-muted-foreground">Produk</p>
                  <p className="font-semibold">{stockModal.product.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">Stok saat ini: <span className="font-bold text-foreground">{stockModal.product.stock} {stockModal.product.unit}</span></p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {stockModal.mode === 'add' ? 'Jumlah Tambah' : 'Jumlah Kurangi'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={stockModal.mode === 'subtract' ? stockModal.product.stock : undefined}
                    className="input-field"
                    placeholder="Masukkan jumlah..."
                    value={stockAmount}
                    onChange={(e) => setStockAmount(e.target.value)}
                    autoFocus
                    data-testid="stock-amount-input"
                  />
                  {stockModal.mode === 'subtract' && stockAmount && parseInt(stockAmount) > stockModal.product.stock && (
                    <p className="text-xs text-rose-500 mt-1">Jumlah melebihi stok yang tersedia</p>
                  )}
                </div>

                {stockAmount && parseInt(stockAmount) > 0 && (
                  <div className="p-3 bg-blue-50 rounded-xl text-sm">
                    <p className="text-blue-600">
                      Stok setelah update: <span className="font-bold">
                        {stockModal.mode === 'add' 
                          ? stockModal.product.stock + parseInt(stockAmount)
                          : Math.max(0, stockModal.product.stock - parseInt(stockAmount))
                        } {stockModal.product.unit}
                      </span>
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setStockModal(null)}
                    className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
                    data-testid="stock-modal-cancel"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleStockSubmit}
                    disabled={!stockAmount || parseInt(stockAmount) <= 0 || (stockModal.mode === 'subtract' && parseInt(stockAmount) > stockModal.product.stock)}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      stockModal.mode === 'add' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
                    }`}
                    data-testid="stock-modal-submit"
                  >
                    {stockModal.mode === 'add' ? 'Tambah Stok' : 'Kurangi Stok'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============== REPORT MODULE ==============

const reportTabs = [
  { key: 'customers', label: 'Pelanggan', icon: Users },
  { key: 'suppliers', label: 'Supplier', icon: Truck },
  { key: 'products', label: 'Produk', icon: Package },
  { key: 'po', label: 'Purchase Order', icon: ClipboardList },
  { key: 'inventory', label: 'Inventory', icon: Archive },
  { key: 'quotation', label: 'Quotation', icon: ClipboardList },
  { key: 'invoice', label: 'Invoice', icon: Receipt },
  { key: 'sj', label: 'Surat Jalan', icon: Truck },
];


export default InventoryModule;
